import React, { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { adminApi, staffLogin, apiErr, ADMIN_TOKEN_KEY } from "@/lib/authApi";

// Module-level cache so the auth check doesn't flash the login screen on every
// route change. Each admin page is a top-level route and remounts the hook —
// without this cache, every nav would re-run `/auth/me` and briefly render
// <AdminLogin />. We seed from localStorage synchronously, revalidate once in
// the background, and reuse the result across remounts.
const PLACEHOLDER = { pending: true };
let _cachedUser = undefined;      // undefined=never checked, null=no token, object=valid
let _revalidatePromise = null;    // in-flight `/auth/me` promise, if any
const _listeners = new Set();     // notifies all mounted hooks when the cache updates

function _hasToken() {
  try {
    return !!(localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem("bni-devadmin-token"));
  } catch { return false; }
}

function _notify() { _listeners.forEach((fn) => fn(_cachedUser)); }

function _ensureRevalidation() {
  if (_revalidatePromise) return _revalidatePromise;
  _revalidatePromise = adminApi.get("/auth/me")
    .then((r) => { _cachedUser = r.data; _notify(); return r.data; })
    .catch(() => {
      try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch {}
      _cachedUser = null; _notify(); return null;
    })
    .finally(() => { _revalidatePromise = null; });
  return _revalidatePromise;
}

function _initialUser() {
  if (_cachedUser !== undefined) return _cachedUser;      // reuse validated/cached
  if (!_hasToken()) { _cachedUser = null; return null; }  // no token → login
  return PLACEHOLDER;                                     // optimistic: render page
}

export function useAdminSession() {
  const [user, setUserState] = useState(_initialUser);

  useEffect(() => {
    _listeners.add(setUserState);
    // If we're optimistic (placeholder), kick off a one-time revalidation
    if (user === PLACEHOLDER || _cachedUser === PLACEHOLDER || (_hasToken() && _cachedUser === undefined)) {
      _ensureRevalidation();
    }
    return () => { _listeners.delete(setUserState); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUser = (u) => { _cachedUser = u; _notify(); };
  const logout = () => {
    try { localStorage.removeItem(ADMIN_TOKEN_KEY); } catch {}
    _cachedUser = null; _notify();
  };
  return { user, setUser, logout };
}

export function AdminLogin({ onSuccess }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const data = await staffLogin(phone, password);
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      onSuccess(data.user);
    } catch (ex) { setErr(apiErr(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Panel className="p-8 w-full max-w-md" data-testid="admin-login-panel">
        <div className="flex justify-center mb-5" data-testid="admin-login-logo-wrap">
          <img src="/risse-bni-dark.webp" alt="RISSE BNI 26 · Davanagere Members Day" className="h-28 w-auto" data-testid="admin-login-logo" />
        </div>
        <Eyebrow>Admin · Command Dashboard</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl sm:text-4xl">Sign in to run the day.</h1>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <MonoLabel>Phone number</MonoLabel>
            <input data-testid="admin-login-phone" className="input-x mt-2 !font-mono" placeholder="10-digit mobile" value={phone} maxLength={10} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} autoFocus />
          </div>
          <div>
            <MonoLabel>Password</MonoLabel>
            <input data-testid="admin-login-password" type="password" className="input-x mt-2" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {err && <div data-testid="admin-login-error" className="text-sm" style={{ color: "var(--red-2)" }}>{err}</div>}
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="btn-red w-full justify-center"><Lock size={14} /> {busy ? "Signing in…" : "Sign in"}</button>
        </form>
      </Panel>
    </div>
  );
}
